import type { ReactNode } from 'react';
import { useSeatPhones, type SeatPhone } from '../../lib/api/hooks-extension.js';
import { Badge } from '../../components/ui/Primitives.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Every phone signed in for one seat, opened from its row (owner, 23 September 2026: "how do I
 * know if they are online or not").
 *
 * A seat's badge can only be about the line it answers as. With an app registered through the
 * push server beside a desk phone on the seat's own login, the seat read as online on the push
 * server and the handset went unmentioned. Here each name stands on its own: what it is, whether
 * it is registered now, from where, and when it was last seen if it is not.
 *
 * These are ROWS OF THE SAME TABLE, on the seat list's own columns (owner, 23 September 2026:
 * they "float and don't sit under the headings"). They were a block of their own inside one
 * spanning cell, with their own spacing, so the same four facts -- state, phone, address -- sat
 * beside the headings that name them instead of under them. A phone is a line of the seat list
 * that happens to be indented, so it is laid out as one.
 */
export function SeatPhones({ tenantId, extensionId, columns }: {
  tenantId: string;
  extensionId: string;
  /** The parent table's column count, so a message spans the row. */
  columns: number;
}) {
  const q = useSeatPhones(tenantId, extensionId);

  if (q.data === undefined) return <Note columns={columns}><span className="arc-skeleton" style={{ display: 'block', height: 34 }} /></Note>;
  if (!q.data.known) {
    return <Note columns={columns}>No node has reported on this account, so nothing is known about its phones.</Note>;
  }
  if (q.data.data.length === 0) {
    return <Note columns={columns}>This extension has no phones set up yet.</Note>;
  }

  return (
    <>
      {q.data.data.map((p) => <Phone key={p.username} p={p} columns={columns} />)}
    </>
  );
}

function Note({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <tr className="arc-seat-row">
      <td className="arc-seat-rail" />
      <td colSpan={columns - 1} className="arc-subtle" style={{ fontSize: 12.5 }}>{children}</td>
    </tr>
  );
}

/** What a name is, in the words the portal uses elsewhere. */
const KIND: Record<SeatPhone['kind'], string> = {
  extension: 'This extension’s own login',
  registration: 'Additional registration',
  app: 'ArcSIP app',
  handset: 'Desk phone',
  browser: 'Workspace phone',
};

function Phone({ p, columns }: { p: SeatPhone; columns: number }) {
  const state = !p.registered ? 'offline' : p.reachable ? 'online' : 'unreachable';
  // Extension, status, phone, IP, then whatever the parent adds after them.
  const trailing = Math.max(0, columns - 5);
  return (
    <tr className="arc-seat-row">
      {/* The chevron's column, carrying the line that ties these rows to the seat above. */}
      <td className="arc-seat-rail">
        <span className="arc-seat-phone-dot" data-state={state} aria-hidden="true" />
      </td>
      <td className="arc-seat-name">
        <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
          <span className="arc-mono" style={{ fontWeight: 550 }}>{p.username}</span>
          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
            {p.label ? `${p.label} · ` : ''}{KIND[p.kind]}
            {p.ring_enabled === false ? ' · does not ring' : ''}
          </span>
        </div>
      </td>
      <td>
        <div className="arc-stack" style={{ gap: 3 }}>
          <div className="arc-row" style={{ gap: 6 }}>
            {state === 'online' ? <Badge tone="ok">online</Badge>
              : state === 'unreachable' ? <Badge tone="warn">not answering</Badge>
                : <Badge tone="muted">offline</Badge>}
            {p.via_push && (
              <span title="The push server holds this registration and wakes the phone for a call.">
                <Badge tone="info">push</Badge>
              </span>
            )}
          </div>
          {!p.registered && (
            <span className="arc-subtle" style={{ fontSize: 11.5 }}>
              {p.offline_since || p.last_seen_at
                ? `last seen ${relativeTime(p.offline_since ?? p.last_seen_at!)}`
                : 'never registered'}
            </span>
          )}
        </div>
      </td>
      <td className="arc-muted">
        {p.user_agent
          ? <span className="arc-truncate" title={p.user_agent}>{p.user_agent}</span>
          : <span className="arc-subtle">—</span>}
      </td>
      <td className="arc-muted">
        {p.registered && p.ip
          ? <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
              <span className="arc-truncate arc-mono" title={p.ip}>{p.ip}</span>
              {(p.transport || p.ping_ms !== null) && (
                <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                  {p.transport ? p.transport.toUpperCase() : ''}
                  {p.transport && p.ping_ms !== null ? ' · ' : ''}
                  {p.ping_ms !== null ? `${Math.round(p.ping_ms)} ms` : ''}
                </span>
              )}
            </div>
          : <span className="arc-subtle">—</span>}
      </td>
      {/* Department and type belong to the seat, not to one of its phones. */}
      {Array.from({ length: trailing }, (_, i) => <td key={i} />)}
    </tr>
  );
}
